import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSelect } from '@/components/ui/common-select';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import Pagination from '@/components/ui/pagination';
import { useToast } from '@/components/ui/use-toast';
import { usePagination } from '@/hooks/usePagination';
import reportService from '@/services/reportService';
import masterDataService from '@/services/masterDataService';
import productService from '@/services/productService';
import RakeMovementReportTable from './RakeMovementReportTable';

const RakeMovementReport = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 100 });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      start_date: params.get('start_date') || '',
      end_date: params.get('end_date') || '',
      rake_id: params.get('rake_id') || '',
      product_type_id: params.get('product_type_id') || '',
    };
  });
  const [errors, setErrors] = useState({
    start_date: '',
    end_date: '',
  });
  const [masterData, setMasterData] = useState({ rakePoints: [], products: [] });
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    if (!searchParams.get('page')) {
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [rakePoints, products] = await Promise.all([
          masterDataService.getRakePoints('UNLOADING_POINT'),
          productService.getProducts(0, 500, true),
        ]);
        const rakePointsData = Array.isArray(rakePoints) ? rakePoints : (rakePoints.data || []);
        const productsData = Array.isArray(products) ? products : (products.data || []);
        setMasterData({ rakePoints: rakePointsData, products: productsData });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load filter options. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsInitialLoad(false);
      }
    };
    loadMasterData();
  }, [toast]);

  useEffect(() => {
    if (!filters.start_date || !filters.end_date) {
      setReports([]);
      setTotalReports(0);
      return;
    }
    handleGenerateReport(true);
  }, [
    currentPage,
    pageSize,
    filters.start_date,
    filters.end_date,
    filters.rake_id,
    filters.product_type_id,
  ]);

  const updateUrlWithFilters = (newFilters) => {
    const params = new URLSearchParams(searchParams);
    ['start_date', 'end_date', 'rake_id', 'product_type_id'].forEach((key) => {
      if (newFilters[key]) params.set(key, newFilters[key]);
      else params.delete(key);
    });
    params.set('page', '1');
    setSearchParams(params);
  };

  const rakePointOptions = masterData.rakePoints
    .filter((rp) => rp && (rp.id || rp.rake_point_id))
    .map((rp) => ({
      value: (rp.id || rp.rake_point_id).toString(),
      label: rp.name || rp.rake_point_name || rp.code || 'Unknown',
    }));

  const productOptions = masterData.products
    .filter((p) => p && p.id)
    .map((p) => ({
      value: p.id.toString(),
      label: p.product_name || p.name || 'Unknown',
    }));

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);

    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const handleDateChange = (key, value) => {
    if (value) {
      handleFilterChange(key, value.split('T')[0]);
    } else {
      handleFilterChange(key, '');
    }

    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!filters.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!filters.end_date) {
      newErrors.end_date = 'End date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildApiFilters = (skip, limit) => {
    const apiFilters = { ...filters, skip, limit };
    if (!apiFilters.rake_id) delete apiFilters.rake_id;
    if (!apiFilters.product_type_id) delete apiFilters.product_type_id;
    if (!apiFilters.start_date) delete apiFilters.start_date;
    if (!apiFilters.end_date) delete apiFilters.end_date;
    return apiFilters;
  };

  const handleGenerateReport = async (isAutoGenerate = false) => {
    if (isAutoGenerate && (!filters.start_date || !filters.end_date)) {
      return;
    }

    if (!isAutoGenerate && !validateForm()) {
      return;
    }

    setGenerating(true);
    try {
      const urlPage = parseInt(searchParams.get('page'), 10) || 1;
      const urlSize = parseInt(searchParams.get('size'), 10) || 100;
      const calculatedSkip = isAutoGenerate ? (urlPage - 1) * urlSize : (currentPage - 1) * pageSize;
      const calculatedLimit = isAutoGenerate ? urlSize : pageSize;

      const response = await reportService.getRakeMovementReports(
        buildApiFilters(calculatedSkip, calculatedLimit)
      );

      const formattedReports = reportService.formatRakeMovementReportData(response.data || []);
      setReports(formattedReports);
      setTotalReports(response.total ?? formattedReports.length);

      if (!isAutoGenerate) {
        toast({
          title: 'Success',
          description: `Generated report with ${formattedReports.length} records.`,
        });
      }
    } catch (error) {
      if (!isAutoGenerate) {
        toast({
          title: 'Error',
          description: `Failed to generate report: ${error.message || 'Please try again.'}`,
          variant: 'destructive',
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleExportReport = async () => {
    if (!validateForm()) {
      return;
    }

    setExporting(true);
    try {
      const blob = await reportService.exportRakeMovementReportExcel(buildApiFilters());
      const filename = `rake-movement-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'Report exported to Excel successfully.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to export report: ${error.message || 'Please try again.'}`,
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  return (
    <div className="space-y-6 relative">
      <FullScreenLoader isLoading={exporting} />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Rake Movement Report</h1>
          <p className="text-xs text-muted-foreground">
            View rake movement dispatch and receipt data with variance tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CommonButton onClick={() => handleGenerateReport(false)} disabled={generating}>
            Generate Report
          </CommonButton>
          {reports.length > 0 && (
            <CommonButton onClick={handleExportReport} disabled={exporting} variant="outline">
              Export to Excel
            </CommonButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative">
        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'start_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="Start Date"
              id="start_date"
              value={filters.start_date ? `${filters.start_date}T00:00` : ''}
              onChange={(e) => handleDateChange('start_date', e.target.value)}
              error={errors.start_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(open ? 'start_date' : (activeDropdown === 'start_date' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'end_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="End Date"
              id="end_date"
              value={filters.end_date ? `${filters.end_date}T00:00` : ''}
              onChange={(e) => handleDateChange('end_date', e.target.value)}
              error={errors.end_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(open ? 'end_date' : (activeDropdown === 'end_date' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'rake_id' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Rake Point"
              id="rake_id"
              value={filters.rake_id}
              onValueChange={(value) => handleFilterChange('rake_id', value)}
              options={rakePointOptions}
              placeholder="All rake points"
              searchable
              onOpenChange={(open) => setActiveDropdown(open ? 'rake_id' : (activeDropdown === 'rake_id' ? null : activeDropdown))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'product_type_id' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Product Type"
              id="product_type_id"
              value={filters.product_type_id}
              onValueChange={(value) => handleFilterChange('product_type_id', value)}
              options={productOptions}
              placeholder="All products"
              searchable
              onOpenChange={(open) => setActiveDropdown(open ? 'product_type_id' : (activeDropdown === 'product_type_id' ? null : activeDropdown))}
            />
          </div>
        </div>
      </div>

      {reports.length > 0 && (
        <TableBlurLoader isLoading={generating}>
          <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform mt-4" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
            <div className="border border-border py-4 rounded-[16px] bg-background">
              <RakeMovementReportTable reports={reports} />
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalReports / pageSize)}
              totalItems={totalReports}
              pageSize={pageSize}
              onPageChange={setPage}
              className="border-t border-border pt-4"
            />
          </div>
        </TableBlurLoader>
      )}
    </div>
  );
};

export default RakeMovementReport;
